import type { components } from "./types/voislog_api";

// 廃止済みエンジン種別（Core APIスキーマには存在しない）
type ObsoleteEngineType = "mdvi" | "cf" | "mimosys";

export type EngineType =
  components["schemas"]["EngineType"] | ObsoleteEngineType;

export const engineTypes = {
  Cognitive: {
    engines: ["cog", "co1", "np1"],
    legacy: true,
  },
  cog: {
    engines: ["cog", "mi1", "mi2"],
    legacy: true,
  },
  co1: {
    engines: ["co1"],
    legacy: false,
  },
  np1: {
    engines: ["np1"],
    legacy: true,
  },
  mi1: {
    engines: ["mi1"],
    legacy: false,
  },
  Mi1_Mi2: {
    engines: ["mi1", "mi2.1"],
    legacy: false,
  },
  mi2: {
    engines: ["mi2"],
    legacy: false,
  },
  "mi2.1": {
    engines: ["mi2.1"],
    legacy: false,
  },
  "co2.1": {
    engines: ["co2.1"],
    legacy: false,
  },
  dysphagia: {
    engines: ["dysphagia"],
    legacy: true,
  },
  br1: {
    engines: ["br1"],
    legacy: true,
  },
  UIX: {
    engines: ["mi1", "mi2", "co1", "br1"],
    legacy: true,
  },
  Co1Demo: {
    engines: ["co1"],
    legacy: false,
  },
  sl1: {
    engines: ["sl1"],
    legacy: false,
  },
  Mi2Demo: {
    engines: ["mi2"],
    legacy: false,
  },
  Co2Demo: {
    engines: ["co2"],
    legacy: false,
  },
  TFH: {
    engines: ["mi2", "co2"],
    legacy: false,
  },
  NTT: {
    engines: ["mi1"],
    legacy: false,
  },
  TokioDRSl1: {
    engines: ["sl1"],
    legacy: false,
  },
  TokioDRCognitive: {
    engines: ["cog", "co1"],
    legacy: true,
  },
} as const;

export type AnalysisEngineType = keyof typeof engineTypes;

export type SupportedAnalysisEngineType = {
  [K in AnalysisEngineType]: (typeof engineTypes)[K]["legacy"] extends true
    ? never
    : K;
}[AnalysisEngineType];

export function isSupportedAnalysisEngineType(
  engineType: AnalysisEngineType,
): engineType is SupportedAnalysisEngineType {
  return !engineTypes[engineType].legacy;
}

export const supportedAnalysisEngineTypes = Object.entries(engineTypes)
  .filter(([, config]) => !config.legacy)
  .map(([engineType]) => engineType) as SupportedAnalysisEngineType[];

export function isKnownAnalysisEngineType(
  engineType: unknown,
): engineType is AnalysisEngineType {
  return typeof engineType === "string" && engineType in engineTypes;
}

export type LanguageType = "ja" | "en";
export function isValidLanguageType(lang: unknown): lang is LanguageType {
  if (typeof lang !== "string") return false;
  return lang === "ja" || lang === "en";
}

export function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x != null && !Array.isArray(x);
}

export type AppApiError = {
  status_code?: number;
  detail: string | Record<string, string> | Array<Record<string, string>>;
};
export function isValidAppApiError(err: unknown): err is AppApiError {
  if (!isObject(err)) {
    return false;
  }
  return (
    (typeof err.status_code !== "number" ||
      typeof err.status_code !== "undefined") &&
    (typeof err.detail === "string" ||
      isObject(err.detail) ||
      Array.isArray(err.detail))
  );
}

export type ResultMainParam = {
  title: string;
  subtitle?: string;
  value: number | string | undefined;
  border?: string;
  maxWidth?: string;
  valueNode?: React.ReactNode;
};

export type ResultSubParam = {
  title: string;
  value: number | string | undefined;
  color?: string;
};

export type ResultSupplementaryInfo = {
  title: string;
  value: number | undefined;
};

export type TableAlign = "left" | "center";

export type ResultDiagramIndex = {
  title: string;
  subTitle?: string;
  width: string;
  subWidth?: string;
  align?: TableAlign;
  textTransform?: "none" | "capitalize" | "uppercase" | "lowercase";
};

export type ResultDiagramParam = {
  width?: string;
  value: string | number;
  align?: TableAlign;
  backgroundColor?: string;
  color?: string;
  height?: string;
  paddingY?: number;
  fontSize?: string;
};

export type ResultDashboardData = {
  mainParam: ResultMainParam;
  subParams: ResultSubParam[];
  supplementaryInfo: ResultSupplementaryInfo[];
  diagramIndex: ResultDiagramIndex[];
  diagramParams: ResultDiagramParam[][];
  phrases?: string[];
};

export type DysphagiaResultSummary = {
  supplementaryInfo: ResultSupplementaryInfo[];
  imgUrl: string;
  result: string;
  score: string;
  comment: string | undefined;
  resultReliabilityMessage?: string;
};

export type AnalysisErrorReason = {
  code: string;
  message: string;
};

export function isValidAnalysisErrorReason(
  reason: unknown,
): reason is AnalysisErrorReason {
  if (!isObject(reason)) return false;
  return typeof reason.code === "string" && typeof reason.message === "string";
}

export type SelectionLoginUser = {
  id?: number;
  email: string;
  password: string;
  nickname: string;
  color: string;
};

export type RecordedData = {
  file?: File;
  data: Float32Array;
  samplingRate: number;
  phrase: string;
  isSkipped: boolean;
  co1VoiceType?: string;
  dysphagiaVoiceTypes?: string;
  br1VoiceType?: string;
  sl1VoiceType?: string;
  co2VoiceType?: string;
  co2_1VoiceType?: string;
};

export type AccessToken = {
  user_id: string;
  company_id: string;
  access_token: string;
  expired_at: string;
};

export type AnalysisState = "IDLE" | "UPLOADING" | "ANALYZING";

export type SoundVisualizerRef = {
  draw: (
    rawDara: number,
    isTooLoud: boolean,
    isDetectedVolume: boolean,
  ) => void;
};

export type PhraseText = {
  title: string;
  phrase: string;
  helperText?: string;
  baseTextSize?: string;
  mdTextSize?: string;
  helperTextSize?: string;
  co1VoiceType?: string;
  br1VoiceType?: string;
  sl1VoiceType?: string;
  co2VoiceType?: string;
  co2_1VoiceType?: string;
};

export type AppType = "base" | "sompo" | "uix" | "ntt" | "tokio-dr";

export type themeType = "sakura_light" | "uix_light" | "ntt_light";

export type CheckVolumeResult =
  | "OK"
  | "Error.allDataIsSilent"
  | "Error.belowVolumeThreshold"
  | "Error.cannotDetectVoice";

type MeanResultCommon = {
  data_path: string;
  num: number;
  mean?: number;
};

export type MeanResult = MeanResultCommon[];

export type LoginFormData = {
  email: string;
  password: string;
};

export type CredentialResponseType = {
  credential: string;
};

export type LoginErrorCode =
  "INVALID_TOKEN" | "INVALID_GOOGLE_TOKEN" | "ENGINE_DISABLED";

export enum LoginMethod {
  LoginForm = "loginForm",
  GoogleAuth = "googleAuth",
}

export interface AuthData {
  [LoginMethod.LoginForm]: LoginFormData;
  [LoginMethod.GoogleAuth]: CredentialResponseType;
}
